import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Eye, Edit, Printer, Trash2, FileText, Filter, X, Calendar, ChevronLeft, ChevronRight, FileDown, Archive, Lock, Truck, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { PasswordInput } from "@/components/ui/password-input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Derive the "target delivery quantity" for a Work Order.
 * Priority:
 *   1. Process 10 output — the user explicitly wants delivery to be capped at
 *      the actual BOX quantity Process 10 produced (NOT bundle count). Prefer
 *      the manual Process Flow Loss entry (process_actuals.process10.pfl_actual),
 *      then the structured actual_total_boxes, then the calculated total_boxes
 *      (boxes_in_bundle × total_bundles).
 *   2. Fallback: header.required_quantity (legacy behaviour for WOs without
 *      Process 10 configured).
 * Returns a non-negative integer; 0 means "no target set → open-ended".
 */
const getDeliveryTargetQty = (wo) => {
  if (!wo) return 0;
  const pa = wo.process_actuals?.process10 || {};
  const p10 = wo.processes?.process10 || {};

  // Runtime fallback: legacy WOs may have boxes_in_bundle + total_bundles
  // saved without an explicit total_boxes field. Compute it on the fly so
  // the dialog never falls back to bundle count.
  const p10ComputedBoxes = (() => {
    const bib = parseInt(p10.boxes_in_bundle) || 0;
    const tb = parseInt(p10.total_bundles) || 0;
    return bib > 0 && tb > 0 ? bib * tb : 0;
  })();

  const candidates = [
    pa.pfl_actual,
    pa.actual_total_boxes,
    p10.actual_total_boxes,
    p10.total_boxes,             // ← actual box count saved by current WO form
    p10ComputedBoxes,            // ← computed from bundle fields for legacy WOs
    wo.header?.required_quantity,
  ];
  for (const v of candidates) {
    if (v === null || v === undefined || v === "") continue;
    const n = parseInt(v);
    if (!isNaN(n) && n > 0) return n;
  }
  return 0;
};

const SavedWorkOrders = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [filteredWorkOrders, setFilteredWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Filter states
  const [filterWONumber, setFilterWONumber] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  
  // Export and Archive states
  const [isExporting, setIsExporting] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [archivePassword, setArchivePassword] = useState("");
  const [archiveDays, setArchiveDays] = useState(365);
  const [isArchiving, setIsArchiving] = useState(false);
  
  // Delivered dialog state
  const [showDeliveredDialog, setShowDeliveredDialog] = useState(false);
  const [deliveredWOId, setDeliveredWOId] = useState(null);
  const [deliveredWO, setDeliveredWO] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [qtyDelivered, setQtyDelivered] = useState("");
  const [actualDeliveryDate, setActualDeliveryDate] = useState("");
  
  // Toggle mode for delivery dialog: 'committed' or 'partial'
  const [deliveryMode, setDeliveryMode] = useState("partial");
  const [committedDeliveryDate, setCommittedDeliveryDate] = useState("");
  const [committedDeliveryComment, setCommittedDeliveryComment] = useState("");
  
  // Pagination states
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Detect if we're in admin or workorder context
  const basePath = location.pathname.startsWith('/workorder') ? '/workorder' : '/admin';

  useEffect(() => {
    fetchWorkOrders();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [workOrders, filterWONumber, filterDate, filterCustomer, filterStatus]);

  const fetchWorkOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/work-orders`);
      setWorkOrders(response.data);
      setFilteredWorkOrders(response.data);
    } catch (error) {
      console.error("Error fetching work orders:", error);
      toast.error("Failed to load work orders");
    } finally {
      setLoading(false);
    }
  };

  const handleView = (workOrder) => {
    navigate(`${basePath}/view/${workOrder.id}`);
  };

  const handleEdit = (workOrder) => {
    navigate(`${basePath}/create-new`, { state: { editData: workOrder } });
  };

  const handlePrint = (workOrder) => {
    // Navigate to view page and trigger print
    navigate(`${basePath}/view/${workOrder.id}`);
  };

  const applyFilters = () => {
    let filtered = [...workOrders];

    // Filter by Work Order Number
    if (filterWONumber) {
      filtered = filtered.filter(wo =>
        wo.header.work_order_number.toLowerCase().includes(filterWONumber.toLowerCase())
      );
    }

    // Filter by Date
    if (filterDate) {
      filtered = filtered.filter(wo =>
        wo.header.date === filterDate
      );
    }

    // Filter by Customer
    if (filterCustomer) {
      filtered = filtered.filter(wo =>
        wo.header.customer_name.toLowerCase().includes(filterCustomer.toLowerCase())
      );
    }

    // Filter by Status
    if (filterStatus !== "all") {
      filtered = filtered.filter(wo => wo.status === filterStatus);
    }

    setFilteredWorkOrders(filtered);
  };

  const clearFilters = () => {
    setFilterWONumber("");
    setFilterDate("");
    setFilterCustomer("");
    setFilterStatus("all");
  };

  const handleStatusChange = async (workOrderId, newStatus) => {
    // For delivered status, show dialog to enter invoice number and qty
    if (newStatus === "delivered") {
      const wo = workOrders.find(w => w.id === workOrderId);
      setDeliveredWO(wo);
      setDeliveredWOId(workOrderId);
      setInvoiceNumber("");
      setInvoiceDate(new Date().toISOString().slice(0, 10));
      setQtyDelivered("");
      // Default the actual delivery date to today (YYYY-MM-DD); user can change
      setActualDeliveryDate(new Date().toISOString().slice(0, 10));
      setDeliveryMode("partial");
      setCommittedDeliveryDate("");
      setCommittedDeliveryComment("");
      setShowDeliveredDialog(true);
      return;
    }
    
    try {
      await axios.patch(`${API}/work-orders/${workOrderId}/status?status=${newStatus}`);
      toast.success("Status updated successfully");
      fetchWorkOrders(); // Refresh the list
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const handleDeliveredConfirm = async () => {
    if (!invoiceNumber.trim()) {
      toast.error("Invoice number is mandatory for delivery");
      return;
    }
    if (!qtyDelivered || parseInt(qtyDelivered) <= 0) {
      toast.error("Quantity delivered is mandatory and must be greater than 0");
      return;
    }
    
    // Use Process 10 output as the cap (falls back to required_quantity, then
    // open-ended). Per user spec: WO should stay 'pending' until the full
    // Process 10 output is delivered.
    const requiredQty = getDeliveryTargetQty(deliveredWO);
    const currentDelivered = deliveredWO?.total_qty_delivered || 0;
    const newQty = parseInt(qtyDelivered);

    // Only enforce the "do not exceed required" cap when a required quantity
    // is actually set on the work order. If required_quantity is missing/0
    // (e.g. older WOs imported without it, or PO not yet finalized) accept
    // whatever the user enters — they obviously know the real shipment count.
    if (requiredQty > 0 && currentDelivered + newQty > requiredQty) {
      toast.error(`Cannot deliver ${newQty}. Total would exceed required quantity (${requiredQty})`);
      return;
    }
    
    try {
      // Send the derived target to the backend so it uses the same cap as
      // the client (Process 10 output) instead of header.required_quantity.
      const targetParam = requiredQty > 0 ? `&target_quantity=${requiredQty}` : "";
      const dateParam = actualDeliveryDate ? `&delivery_date=${encodeURIComponent(actualDeliveryDate)}` : "";
      const invDateParam = invoiceDate ? `&invoice_date=${encodeURIComponent(invoiceDate)}` : "";
      const response = await axios.patch(
        `${API}/work-orders/${deliveredWOId}/status?status=delivered&invoice_number=${encodeURIComponent(invoiceNumber.trim())}&qty_delivered=${newQty}${targetParam}${dateParam}${invDateParam}`
      );
      
      if (response.data.is_partial) {
        if (requiredQty > 0) {
          toast.info(`Partial delivery recorded. ${response.data.total_delivered}/${requiredQty} delivered. Balance: ${response.data.balance}`);
        } else {
          toast.info(`Delivery recorded. Total delivered so far: ${response.data.total_delivered}`);
        }
      } else {
        toast.success("Work order marked as fully delivered!");
      }
      
      setShowDeliveredDialog(false);
      setDeliveredWOId(null);
      setDeliveredWO(null);
      setInvoiceNumber("");
      setInvoiceDate("");
      setQtyDelivered("");
      setActualDeliveryDate("");
      fetchWorkOrders();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const handleCommittedDeliveryConfirm = async () => {
    if (!committedDeliveryDate) {
      toast.error("Please select a committed delivery date");
      return;
    }
    
    try {
      await axios.patch(`${API}/work-orders/${deliveredWOId}/committed-delivery`, {
        committed_delivery_date: committedDeliveryDate,
        delivery_comment: committedDeliveryComment.trim() || null
      });
      
      toast.success("Committed delivery date added successfully");
      
      setShowDeliveredDialog(false);
      setDeliveredWOId(null);
      setDeliveredWO(null);
      setCommittedDeliveryDate("");
      setCommittedDeliveryComment("");
      fetchWorkOrders();
    } catch (error) {
      console.error("Error adding committed delivery:", error);
      toast.error(error.response?.data?.detail || "Failed to add committed delivery");
    }
  };

  const handleDelete = async (workOrderId) => {
    if (!window.confirm("Are you sure you want to delete this work order?")) {
      return;
    }

    try {
      await axios.delete(`${API}/work-orders/${workOrderId}`);
      toast.success("Work order deleted successfully");
      fetchWorkOrders();
    } catch (error) {
      console.error("Error deleting work order:", error);
      toast.error("Failed to delete work order");
    }
  };

  // Export to Excel function
  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      const response = await axios.get(`${API}/export/work-orders`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `work_orders_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Work orders exported successfully!");
    } catch (error) {
      console.error("Error exporting:", error);
      toast.error("Failed to export work orders");
    } finally {
      setIsExporting(false);
    }
  };

  // Archive function (password protected)
  const handleArchive = async () => {
    if (!archivePassword) {
      toast.error("Please enter password");
      return;
    }
    
    try {
      setIsArchiving(true);
      const response = await axios.post(`${API}/archive/work-orders`, {
        password: archivePassword,
        days_old: archiveDays,
        collection: "work_orders"
      });
      
      toast.success(response.data.message);
      setIsArchiveDialogOpen(false);
      setArchivePassword("");
      fetchWorkOrders();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Invalid password");
      } else {
        toast.error("Failed to archive data");
      }
    } finally {
      setIsArchiving(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      available: "bg-green-500/20 text-green-300 border-green-500/40",
      in_progress: "bg-blue-500/20 text-blue-300 border-blue-500/40",
      completed: "bg-gray-500/20 text-gray-300 border-gray-500/40",
      rejected: "bg-red-500/20 text-red-300 border-red-500/40",
      delivered: "bg-purple-500/20 text-purple-300 border-purple-500/40",
      draft: "bg-amber-500/20 text-amber-300 border-amber-500/40",
    };
    return colors[status] || colors.available;
  };

  if (loading) {
    return (
      <div className="min-h-screen p-6 bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900">
        <div className="text-center text-cyan-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900">
      <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-cyan-400 flex items-center gap-3">
            <FileText className="h-8 w-8" />
            Saved Work Orders
          </h1>
          <p className="text-gray-400 mt-1">
            View, edit, print or delete saved work orders
          </p>
        </div>
      </div>

      <Card className="bg-gray-800/50 border border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl text-cyan-400">
              All Work Orders ({filteredWorkOrders.length} of {workOrders.length})
            </CardTitle>
            <div className="flex gap-2 items-center">
              <div className="flex items-center gap-2">
                <Label className="text-gray-400 text-sm">Show:</Label>
                <Select value={pageSize.toString()} onValueChange={(v) => { setPageSize(parseInt(v)); setCurrentPage(1); }}>
                  <SelectTrigger className="w-20 h-8 bg-gray-900 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-600 text-white">
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20"
                onClick={handleExportToExcel}
                disabled={isExporting}
              >
                <FileDown className={`h-4 w-4 mr-2 ${isExporting ? 'animate-pulse' : ''}`} />
                Export
              </Button>
              <Button
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                onClick={() => setIsArchiveDialogOpen(true)}
              >
                <Archive className="h-4 w-4 mr-2" />
                Archive
              </Button>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant="outline"
                className="flex items-center gap-2 border-gray-600 text-gray-300 hover:bg-gray-800"
              >
                <Filter className="h-4 w-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </Button>
              <Button
                onClick={() => navigate(`${basePath}/creation`)}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
              >
                Create New Work Order
              </Button>
            </div>
          </div>
          
          {/* Filter Section */}
          {showFilters && (
            <div className="mt-6 p-4 bg-gray-900/50 border border-gray-700 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* WO Number Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-wo-number" className="text-cyan-300">Work Order Number</Label>
                  <Input
                    id="filter-wo-number"
                    type="text"
                    placeholder="Search WO Number..."
                    value={filterWONumber}
                    onChange={(e) => setFilterWONumber(e.target.value)}
                    className="bg-gray-900 border-gray-600 text-white"
                  />
                </div>

                {/* Date Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-date" className="text-cyan-300">Date</Label>
                  <div className="relative">
                    <Input
                      id="filter-date"
                      type="date"
                      value={filterDate}
                      onChange={(e) => setFilterDate(e.target.value)}
                      className="bg-gray-900 border-gray-600 text-white pr-10 [color-scheme:dark]"
                    />
                    <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-cyan-400 pointer-events-none" />
                  </div>
                </div>

                {/* Customer Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-customer" className="text-cyan-300">Customer Company</Label>
                  <Input
                    id="filter-customer"
                    type="text"
                    placeholder="Search Customer..."
                    value={filterCustomer}
                    onChange={(e) => setFilterCustomer(e.target.value)}
                    className="bg-gray-900 border-gray-600 text-white"
                  />
                </div>

                {/* Status Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-status" className="text-cyan-300">Status</Label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="bg-gray-900 border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-gray-600 text-white">
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="draft">Draft (Data Pending)</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Clear Filters Button */}
              <div className="mt-4 flex justify-end">
                <Button
                  onClick={clearFilters}
                  variant="outline"
                  className="flex items-center gap-2 border-gray-600 text-gray-300"
                  size="sm"
                >
                  <X className="h-4 w-4" />
                  Clear All Filters
                </Button>
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {filteredWorkOrders.length === 0 ? (
            <p className="text-center text-gray-400 py-8">
              {workOrders.length === 0 ? "No work orders found. Create your first work order!" : "No work orders match the filters"}
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                <TableHeader>
                  <TableRow className="border-b border-gray-700 hover:bg-transparent">
                    <TableHead className="text-gray-300">WO Number</TableHead>
                    <TableHead className="text-gray-300">Date</TableHead>
                    <TableHead className="text-gray-300">Customer</TableHead>
                    <TableHead className="text-gray-300">Item</TableHead>
                    <TableHead className="text-gray-300">Quantity</TableHead>
                    <TableHead className="text-gray-300">Status</TableHead>
                    <TableHead className="text-gray-300">Completion History</TableHead>
                    <TableHead className="text-gray-300 text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredWorkOrders.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((wo) => (
                    <TableRow 
                      key={wo.id} 
                      className="border-b border-gray-700 hover:bg-gray-800/30"
                    >
                      <TableCell className="text-white font-semibold">
                        {wo.header?.work_order_number || '-'}
                      </TableCell>
                      <TableCell className="text-white">
                        {wo.header?.work_order_creation_date || '-'}
                      </TableCell>
                      <TableCell className="text-white">
                        {wo.header?.customer_name || '-'}
                      </TableCell>
                      <TableCell className="text-white">
                        {wo.header?.item_name || '-'}
                      </TableCell>
                      <TableCell className="text-white">
                        {(() => {
                          const requiredQty = parseInt(wo.header?.required_quantity || 0);
                          const deliveredQty = wo.total_qty_delivered || 0;
                          const balanceQty = requiredQty - deliveredQty;
                          
                          if (deliveredQty > 0 && balanceQty > 0) {
                            // Show balance quantity for partial deliveries
                            return (
                              <div>
                                <span className="text-yellow-400 font-bold">{balanceQty}</span>
                                <span className="text-gray-500 text-xs block">
                                  (Balance of {requiredQty})
                                </span>
                                <span className="text-green-400 text-xs">
                                  {deliveredQty} delivered
                                </span>
                              </div>
                            );
                          } else if (deliveredQty > 0 && balanceQty === 0) {
                            // Fully delivered
                            return (
                              <div>
                                <span className="text-green-400 font-bold">{requiredQty}</span>
                                <span className="text-green-500 text-xs block">
                                  ✓ Fully delivered
                                </span>
                              </div>
                            );
                          }
                          // No deliveries yet - show original quantity
                          return requiredQty || '-';
                        })()}
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={wo.status || 'available'}
                          onValueChange={(value) => handleStatusChange(wo.id, value)}
                        >
                          <SelectTrigger className={`w-32 text-xs border ${getStatusBadge(wo.status || 'available')}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-900 border-gray-600 text-white">
                            <SelectItem value="available">Available</SelectItem>
                            <SelectItem value="draft">Draft (Data Pending)</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                            <SelectItem value="delivered">Delivered</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-white">
                        {wo.completion_history && wo.completion_history.length > 0 ? (
                          <div className="space-y-1 max-w-xs">
                            {wo.completion_history.map((history, idx) => (
                              <div key={idx} className="text-xs bg-green-500/10 border border-green-500/30 rounded p-2">
                                <div><span className="font-semibold text-green-400">{history.process_name?.toUpperCase()}</span></div>
                                <div className="text-gray-300">
                                  ✓ Completed by <span className="font-semibold text-white">{history.employee_name || 'N/A'}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-500 text-xs">No history</span>
                        )}
                        {wo.invoice_number && (
                          <div className="mt-2 text-xs bg-purple-500/10 border border-purple-500/30 rounded p-2">
                            <span className="text-purple-400 font-semibold">Invoice #:</span>
                            <span className="text-white ml-1">{wo.invoice_number}</span>
                          </div>
                        )}
                        {wo.committed_delivery_date && (
                          <div className="mt-2 text-xs bg-cyan-500/10 border border-cyan-500/30 rounded p-2">
                            <span className="text-cyan-400 font-semibold">Committed Delivery:</span>
                            <span className="text-white ml-1">{new Date(wo.committed_delivery_date).toLocaleDateString()}</span>
                            {wo.delivery_comment && (
                              <div className="text-gray-300 mt-1">{wo.delivery_comment}</div>
                            )}
                          </div>
                        )}
                        {wo.delivery_commitments?.length > 0 && (
                          <div className="mt-2 text-xs bg-cyan-500/10 border border-cyan-500/30 rounded p-2 space-y-1">
                            <span className="text-cyan-400 font-semibold">Delivery Commitments:</span>
                            {wo.delivery_commitments.slice().reverse().map((dc, dcIdx) => (
                              <div key={dcIdx} className="flex gap-2 text-gray-300">
                                <span className="text-cyan-300">{dc.date ? new Date(dc.date).toLocaleDateString() : "-"}</span>
                                {dc.comment && <span>- {dc.comment}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleView(wo)}
                            className="bg-blue-600 hover:bg-blue-700"
                            title="View"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleEdit(wo)}
                            className="bg-cyan-600 hover:bg-cyan-700"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handlePrint(wo)}
                            className="bg-purple-600 hover:bg-purple-700"
                            title="Print"
                          >
                            <Printer className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleDelete(wo.id)}
                            variant="destructive"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
              {/* Pagination Controls */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
                <div className="text-sm text-gray-400">
                  Showing {Math.min((currentPage - 1) * pageSize + 1, filteredWorkOrders.length)} to {Math.min(currentPage * pageSize, filteredWorkOrders.length)} of {filteredWorkOrders.length} entries
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="border-gray-600 text-gray-300 hover:bg-gray-800 disabled:opacity-50"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Prev
                  </Button>
                  <span className="text-cyan-400 px-3">
                    Page {currentPage} of {Math.ceil(filteredWorkOrders.length / pageSize) || 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredWorkOrders.length / pageSize), p + 1))}
                    disabled={currentPage >= Math.ceil(filteredWorkOrders.length / pageSize)}
                    className="border-gray-600 text-gray-300 hover:bg-gray-800 disabled:opacity-50"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      </div>
      
      {/* Delivered Dialog - Toggle between Committed Delivery and Partial Delivery Entry */}
      <Dialog open={showDeliveredDialog} onOpenChange={setShowDeliveredDialog}>
        <DialogContent className="bg-gray-900 border-cyan-500/50 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 text-xl">
              {deliveredWO?.status === 'completed' ? 'Work Order Completed - Add Delivery Entry' : 'Record Delivery'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {deliveredWO && (() => {
              const target = getDeliveryTargetQty(deliveredWO);
              const delivered = deliveredWO.total_qty_delivered || 0;
              const balance = Math.max(0, target - delivered);
              return (
                <div className="p-3 bg-gray-800 rounded-lg border border-gray-700">
                  <p className="text-sm text-gray-400">Work Order: <span className="text-white font-semibold">{deliveredWO.header?.work_order_number}</span></p>
                  <p className="text-sm text-gray-400">Item: <span className="text-white">{deliveredWO.header?.item_name}</span></p>
                  {target > 0 ? (
                    <>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                        <p className="text-sm text-gray-400">P10 Output: <span className="text-cyan-400 font-bold" data-testid="delivery-p10-output">{target}</span></p>
                        <p className="text-sm text-gray-400">Delivered: <span className="text-green-400 font-bold" data-testid="delivery-already-delivered">{delivered}</span></p>
                        <p className="text-sm text-gray-400">Balance: <span className="text-yellow-400 font-bold" data-testid="delivery-balance">{balance}</span></p>
                      </div>
                      <p className="text-[11px] text-gray-500 italic mt-1">
                        WO stays pending until the full P10 output ({target}) is invoiced.
                      </p>
                    </>
                  ) : (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                      <p className="text-sm text-gray-400">Delivered so far: <span className="text-green-400 font-bold">{delivered}</span></p>
                      <p className="text-xs text-amber-300/80 italic">Process 10 output not recorded yet — open-ended delivery</p>
                    </div>
                  )}
                </div>
              );
            })()}
            
            {/* Toggle Section for Committed Delivery vs Partial Delivery */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={deliveryMode === 'committed' ? 'default' : 'outline'}
                onClick={() => setDeliveryMode('committed')}
                className={`flex items-center gap-2 ${
                  deliveryMode === 'committed' 
                    ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                    : 'border-gray-600 text-gray-300 hover:bg-gray-800'
                }`}
                data-testid="delivery-mode-committed"
              >
                <CalendarPlus className="h-4 w-4" />
                Committed Delivery
              </Button>
              <Button
                type="button"
                variant={deliveryMode === 'partial' ? 'default' : 'outline'}
                onClick={() => setDeliveryMode('partial')}
                className={`flex items-center gap-2 ${
                  deliveryMode === 'partial' 
                    ? 'bg-cyan-600 hover:bg-cyan-700 text-white' 
                    : 'border-gray-600 text-gray-300 hover:bg-gray-800'
                }`}
                data-testid="delivery-mode-partial"
              >
                <Truck className="h-4 w-4" />
                Partial Delivery Entry
              </Button>
            </div>
            
            {/* Committed Delivery Section */}
            {deliveryMode === 'committed' && (
              <div className="space-y-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <p className="text-sm text-purple-300">
                  Add a new committed delivery date for this work order
                </p>
                <div>
                  <Label className="text-purple-300">Committed Delivery Date *</Label>
                  <Input
                    type="date"
                    value={committedDeliveryDate}
                    onChange={(e) => setCommittedDeliveryDate(e.target.value)}
                    className="bg-gray-900 border-gray-600 text-white mt-1 [color-scheme:dark]"
                  />
                </div>
                <div>
                  <Label className="text-purple-300">Comment (Optional)</Label>
                  <Textarea
                    value={committedDeliveryComment}
                    onChange={(e) => setCommittedDeliveryComment(e.target.value)}
                    placeholder="Add any notes about this delivery commitment..."
                    className="bg-gray-900 border-gray-600 text-white mt-1"
                    rows={2}
                  />
                </div>
                
                {/* Previous Commitments */}
                {deliveredWO?.delivery_commitments?.length > 0 && (
                  <div className="mt-2">
                    <Label className="text-gray-400 text-sm">Previous Commitments:</Label>
                    <div className="mt-1 max-h-20 overflow-y-auto">
                      {deliveredWO.delivery_commitments.slice().reverse().map((dc, i) => (
                        <p key={i} className="text-xs text-gray-500">
                          {dc.date ? new Date(dc.date).toLocaleDateString() : '-'} {dc.comment && `- ${dc.comment}`}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Partial Delivery Entry Section */}
            {deliveryMode === 'partial' && (
              <div className="space-y-4 p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                <p className="text-sm text-cyan-300">
                  Record actual partial delivery with invoice
                </p>
                <div>
                  <Label className="text-cyan-300">Invoice Number *</Label>
                  <Input
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    placeholder="Enter invoice number"
                    className="bg-gray-900 border-gray-600 text-white mt-1"
                    autoFocus
                  />
                </div>

                {/* Invoice Date — date printed on the invoice itself */}
                <div>
                  <Label className="text-cyan-300">Invoice Date *</Label>
                  <Input
                    type="date"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    className="bg-gray-900 border-gray-600 text-white mt-1 [color-scheme:dark]"
                    data-testid="invoice-date"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Date printed on the invoice (defaults to today).
                  </p>
                </div>
                
                <div>
                  <Label className="text-cyan-300">Quantity Delivered *</Label>
                  <Input
                    type="number"
                    value={qtyDelivered}
                    onChange={(e) => setQtyDelivered(e.target.value)}
                    placeholder="Enter quantity delivered"
                    className="bg-gray-900 border-gray-600 text-white mt-1"
                    min="1"
                    max={(() => {
                      const t = getDeliveryTargetQty(deliveredWO);
                      return t > 0 ? Math.max(0, t - (deliveredWO?.total_qty_delivered || 0)) : undefined;
                    })()}
                  />
                  {(() => {
                    const t = getDeliveryTargetQty(deliveredWO);
                    if (t > 0) {
                      const max = Math.max(0, t - (deliveredWO?.total_qty_delivered || 0));
                      return (
                        <p className="text-xs text-gray-500 mt-1">
                          Max deliverable: {max}
                          {max === 0 && <span className="ml-2 text-emerald-400">(all delivered)</span>}
                        </p>
                      );
                    }
                    return (
                      <p className="text-xs text-amber-300/70 mt-1">
                        No P10 output recorded — enter the actual delivered quantity from the invoice.
                      </p>
                    );
                  })()}
                </div>

                {/* Actual Delivery Date */}
                <div>
                  <Label className="text-cyan-300">Delivery Date *</Label>
                  <Input
                    type="date"
                    value={actualDeliveryDate}
                    onChange={(e) => setActualDeliveryDate(e.target.value)}
                    className="bg-gray-900 border-gray-600 text-white mt-1 [color-scheme:dark]"
                    data-testid="delivery-actual-date"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Date the goods physically left the dock (defaults to today).
                  </p>
                </div>
                
                {deliveredWO?.delivery_history?.length > 0 && (
                  <div className="mt-2">
                    <Label className="text-gray-400 text-sm">Previous Deliveries:</Label>
                    <div className="mt-1 max-h-20 overflow-y-auto">
                      {deliveredWO.delivery_history.map((d, i) => (
                        <p key={i} className="text-xs text-gray-500">
                          Inv: {d.invoice_number}
                          {d.invoice_date ? ` (${new Date(d.invoice_date).toLocaleDateString()})` : ""}
                          {" | Qty: "}{d.qty_delivered}
                          {" | Delivered: "}
                          {d.delivery_date
                            ? new Date(d.delivery_date).toLocaleDateString()
                            : new Date(d.timestamp).toLocaleDateString()}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowDeliveredDialog(false)} 
              className="border-gray-600 text-gray-300"
            >
              Cancel
            </Button>
            {deliveryMode === 'committed' ? (
              <Button 
                onClick={handleCommittedDeliveryConfirm} 
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
              >
                <CalendarPlus className="h-4 w-4 mr-2" />
                Add Commitment
              </Button>
            ) : (
              <Button 
                onClick={handleDeliveredConfirm} 
                className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold"
              >
                <Truck className="h-4 w-4 mr-2" />
                Confirm Delivery
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Dialog (Password Protected) */}
      <Dialog open={isArchiveDialogOpen} onOpenChange={setIsArchiveDialogOpen}>
        <DialogContent className="bg-gray-900 border-red-500/50 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-400 text-xl flex items-center gap-2">
              <Archive className="h-5 w-5" />
              Archive Old Work Orders
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-300 text-sm flex items-center gap-2">
                <Lock className="h-4 w-4" />
                This action will archive (move to backup) old COMPLETED work orders only.
              </p>
            </div>
            
            <div>
              <Label className="text-gray-300">Archive completed WOs older than (days)</Label>
              <Input
                type="number"
                value={archiveDays}
                onChange={(e) => setArchiveDays(parseInt(e.target.value) || 365)}
                className="bg-gray-900 border-gray-600 text-white mt-1"
                min={30}
              />
              <p className="text-xs text-gray-500 mt-1">
                Completed WOs dated before {new Date(Date.now() - archiveDays * 24 * 60 * 60 * 1000).toLocaleDateString()} will be archived
              </p>
            </div>
            
            <div>
              <Label className="text-gray-300">Enter Password to Confirm</Label>
              <PasswordInput
                value={archivePassword}
                onChange={(e) => setArchivePassword(e.target.value)}
                className="bg-gray-900 border-gray-600 text-white mt-1"
                placeholder="Enter archival password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { setIsArchiveDialogOpen(false); setArchivePassword(""); }}
              className="border-gray-600 text-gray-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleArchive}
              disabled={isArchiving || !archivePassword}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {isArchiving ? "Archiving..." : "Archive Data"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default SavedWorkOrders;
